import React, {useEffect, useRef, useState} from 'react';
import {InputText} from 'primereact/inputtext';
import {InputTextarea} from 'primereact/inputtextarea';
import {Dropdown} from 'primereact/dropdown';
import {MultiSelect} from 'primereact/multiselect';
import {AutoComplete} from 'primereact/autocomplete';
import {TriStateCheckbox} from 'primereact/tristatecheckbox';
import {Checkbox} from 'primereact/checkbox';
import {recruitmentApi} from '../recruitmentApi';
import {candidateProfileGroups, candidateSearchGroups} from './candidateProfileSchema';
import {candidatePhone} from './candidateDisplay';

export default function CandidateProfileSection({mode = 'edit', values, onChange, t, disabled = false}) {
  const [options, setOptions] = useState({});
  const [cities, setCities] = useState([]);
  const [error, setError] = useState(false);
  const cityRequest = useRef(0);
  useEffect(() => {
    if (mode === 'view') return;
    let active = true;
    recruitmentApi('get', '/candidate/profile-options').then(r => {
      if (active) setOptions(r.data.data);
    }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [mode]);
  const label = f => t(`candidateProfile.fields.${f.name}`, {defaultValue: f.label});
  const renderField = f => {
    const value = values[f.name];
    const id = `candidate-${mode}-${f.name}`;
    const change = value => onChange(f.name, value);
    const common = {id, disabled, className: 'p-inputtext-sm'};
    if (mode === 'view') {
      let display = value;
      if (f.name === 'phone') display = candidatePhone(values);
      if (f.type === 'boolean') display = value == null ? '—' : t(value ? 'yes' : 'no');
      else if (Array.isArray(value)) display = value.map(v => v.name).join(', ');
      else if (value && typeof value === 'object') display = value.name;
      else if (f.type === 'date' && value) display = window.App.formatDateValue(value.slice(0, 10));
      return <span className="p-d-block fake-input" style={{overflowWrap: 'anywhere', whiteSpace: 'pre-wrap'}}>{display || '—'}</span>;
    }
    if (f.type === 'boolean') return <div className="p-d-flex p-ai-center" style={{gap: 8, minHeight: '2.5rem'}}>
      {mode === 'edit' && f.required
        ? <Checkbox inputId={id} disabled={disabled} checked={value === true} onChange={e => change(e.checked)}/>
        : <TriStateCheckbox id={id} aria-label={label(f)} disabled={disabled} value={value ?? null} onChange={e => change(e.value)}/>}
      <span>{value == null ? t(mode === 'search' ? 'candidateProfile.any' : 'candidateProfile.unknown') : t(value ? 'yes' : 'no')}</span>
    </div>;
    if (f.type === 'country') return <Dropdown {...common} inputId={id} value={value ?? null} options={options.country || []} optionLabel="name" optionValue="code" filter showClear onChange={e => change(e.value)}/>;
    if (f.type === 'relation' || f.type === 'many') {
      const Component = f.type === 'many' ? MultiSelect : Dropdown;
      const selected = f.type === 'many' ? (value || []).map(v => v.id ?? v) : value?.id ?? value ?? null;
      return <Component {...common} inputId={id} value={selected} options={options[f.options] || []} optionLabel="name" optionValue="id"
        filter showClear onChange={e => change(e.value)} disabled={disabled || error}/>;
    }
    if (f.type === 'city' && mode !== 'search') return <AutoComplete {...common} inputId={id} value={value ?? null} field="name" suggestions={cities} forceSelection
      completeMethod={async e => {
        const request = ++cityRequest.current;
        try {
          const response = await recruitmentApi('get', '/city', undefined, {'search[name][operator]': 'like', 'search[name][value]': `*${e.query}*`, item_per_page: 50});
          if (request === cityRequest.current) setCities(response.data.data.items);
        } catch (error) { window.App.toastShow('error', t('error'), '', '', error.response); }
      }} onChange={e => change(e.value)}/>;
    if (f.type === 'textarea') return <InputTextarea {...common} value={value ?? ''} rows={3} onChange={e => change(e.target.value)}/>;
    return <InputText {...common} type={f.type === 'date' ? 'date' : f.type === 'email' ? 'email' : 'text'}
      maxLength={f.maxLength} required={mode !== 'search' && f.required} value={f.type === 'date' ? (value || '').slice(0, 10) : value ?? ''}
      onChange={e => change(e.target.value)}/>;
  };
  return <>
    {error && <p role="alert" className="p-error">{t('candidateProfile.optionsError', {defaultValue: 'A törzsadatok betöltése nem sikerült. Frissítsd az oldalt.'})}</p>}
    {(mode === 'search' ? candidateSearchGroups : candidateProfileGroups).map(group => <section key={group.key} className="p-mb-4">
      <h3 className="text-primary p-mb-3">{t(`candidateProfile.groups.${group.key}`, {defaultValue: group.label})}</h3>
      <div className="p-grid">{group.fields.filter(f => !f.name.startsWith('student_card_') && (mode === 'edit' || !f.name.startsWith('phone_'))).map(f => <div key={f.name} className={f.type === 'textarea' ? 'p-col-12' : mode === 'view' ? 'p-col-12 p-xl-6' : 'p-col-12 p-md-6 p-xl-4'}>
        <div className="p-field p-fluid"><label htmlFor={`candidate-${mode}-${f.name}`}>{label(f)}{mode === 'edit' && f.required ? ' *' : ''}</label>{renderField(f)}</div>
      </div>)}</div>
    </section>)}
  </>;
}
